import React, { createContext, ReactNode, useContext, useRef, useSyncExternalStore } from 'react';
import { createPieceStore, PieceStore } from './piece-store';
import type { PieceModel } from './types';

const EMPTY_PIECE: PieceModel = {
  id: 'main',
  width: 0,
  height: 0,
  type: 'image',
  fps: 30,
  duration: 0,
  layers: {},
  layerOrder: []
};

const PieceStoreContext = createContext<PieceStore | null>(null);

export const PieceProvider: React.FC<{ initial?: PieceModel; children: ReactNode }> = ({ initial, children }) => {
  const ref = useRef<PieceStore | null>(null);
  if (ref.current === null) ref.current = createPieceStore(initial ?? EMPTY_PIECE);

  return <PieceStoreContext.Provider value={ref.current}>{children}</PieceStoreContext.Provider>;
};

const useStore = (): PieceStore => {
  const store = useContext(PieceStoreContext);
  if (!store) throw new Error('usePiece* must be used inside <PieceProvider>');
  return store;
};

/** Reads a slice of the model and only re-renders when that slice changes. */
export function usePieceSelector<T>(selector: (m: PieceModel) => T, isEqual: (a: T, b: T) => boolean = Object.is): T {
  const store = useStore();
  const last = useRef<{ value: T } | null>(null);

  const getSnapshot = (): T => {
    const next = selector(store.getState());
    if (last.current && isEqual(last.current.value, next)) return last.current.value;
    last.current = { value: next };
    return next;
  };

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}

export const usePieceModel = (): PieceModel => usePieceSelector(m => m);
export const usePieceDispatch = (): PieceStore['dispatch'] => useStore().dispatch;

/** The raw store (getState/subscribe/dispatch) for code that reads outside React's render, like history. */
export const usePieceStore = (): PieceStore => useStore();
